/* ────────────────────────────────────────────────────────────
 * CRM > Admin permission names — Phase 3 "CRM Permission" sheet,
 * rows 1-60, spelled exactly as the backend seeds them (the user
 * permission map is keyed by the name, so a typo silently hides the
 * thing it was meant to gate). "Add As Employee" really does carry
 * the capital A; it is the sheet's spelling, not a slip.
 *
 * Nothing here is new machinery — these are the same string keys the
 * rest of the app already gates on ("View Calls", "View Wallet Admin"
 * …). They live in one file so the three places that enforce them —
 * the sidebar rows, the CRM tab strips, and the action tiles — quote
 * the same constant rather than three loose literals.
 *
 * Where each one is enforced:
 *   crmNav.js            group + tab gates (sidebar rows, tab strips,
 *                        the "+ Add" menu)
 *   QuickActionsSheet.js per-row 3-dot tiles on the list screens
 *   mirror/MirrorPage.js ActionTiles on the detail screens
 *   RecentCalls.js       "View full profile"
 * ──────────────────────────────────────────────────────────── */

export const CRM_PERM = {
  // 1-2 · Recent Calls
  VIEW_RECENT_CALLS: 'View Recent Calls',
  VIEW_CONTACT_PROFILE: 'View Contact Profile',

  // 3 · CRM Dashboard
  VIEW_CRM_DASHBOARD: 'View CRM Dashboard',

  // 4-5 · Leads
  VIEW_RECENT_LEADS: 'View Recent Leads',
  ADD_LEADS: 'Add Leads',

  // 6-17 · Enquiry
  VIEW_ENQUIRY: 'View Enquiry',
  ADD_AS_CANDIDATE: 'Add as Candidate',
  ADD_AS_EMPLOYEE: 'Add As Employee',
  ADD_AS_PROSPECT: 'Add as Prospect',
  ADD_AS_REGISTERED: 'Add as Registered',
  ADD_CONSULTANT: 'Add Consultant',
  ADD_SUPPLIER: 'Add Supplier',
  MARK_AS_SPAM: 'Mark as Spam',
  VIEW_ENQUIRY_CALL: 'View Enquiry Call',
  VIEW_ENQUIRY_EMAIL: 'View Enquiry Email',
  VIEW_ENQUIRY_WHATSAPP: 'View Enquiry Whatsapp',
  VIEW_ENQUIRY_CHAT_BOT: 'View Enquiry Chat Bot',

  // 18-25 · Prospect
  ADD_LEAD: 'Add Lead',
  QUICK_MAIL: 'Quick Mail',
  VIEW_PROSPECT: 'View Prospect',
  VIEW_PROSPECT_CALL: 'View Prospect Call',
  VIEW_PROSPECT_EMAIL: 'View Prospect Email',
  VIEW_PROSPECT_WHATSAPP: 'View Prospect Whatsapp',
  VIEW_REGISTERED: 'View Registered',
  VIEW_PROSPECT_CHAT_BOT: 'View Prospect Chat Bot',

  // 26-29 · Customer
  VIEW_CUSTOMER: 'View Customer',
  VIEW_CUSTOMER_CALL: 'View Customer Call',
  VIEW_CUSTOMER_WEB: 'View Customer Web',
  VIEW_CUSTOMER_WHATSAPP: 'View Customer Whatsapp',

  // 30-43 · Outgoing - Sales
  VIEW_OUTGOING_SALES: 'View Outgoing Sales',
  VIEW_OUTGOING_SALES_BULK_CALL: 'View Outgoing Sales Bulk Call',
  // Row 32. The native Bulk Call screen is a stub and the web has no
  // "Direct" variant of it, so this name is seeded and reserved but gates
  // nothing yet — see BulkCallScreen.js's own header note.
  VIEW_OUTGOING_SALES_BULK_CALL_DIRECT: 'View Outgoing Sales Bulk Call Direct',
  VIEW_SMS_PROMOTION: 'View SMS Promotion',
  VIEW_STORIES_AND_UPDATES: 'View Stories And Updates',
  VIEW_WHATSAPP_PROMOTION: 'View Whatsapp Promotion',
  VIEW_PUSH_NOTIFICATION: 'View Push Notification',
  VIEW_PUSH_GROUPS: 'View Push Groups',
  VIEW_EMAIL_PROMOTION: 'View Email Promotion',
  CREATE_EMAIL_PROMOTION: 'Create Email Promotion',
  CREATE_SMS_PROMOTION: 'Create SMS Promotion',
  CREATE_PUSH_NOTIFICATION: 'Create Push Notification',
  CREATE_PUSH_GROUP: 'Create Push Group',
  // Ampersand, not "And" — the sheet spells rows 34 and 43 differently
  // ("View Stories And Updates" vs "Create Stories & Updates"). Both are
  // reproduced verbatim; neither is a typo to be tidied up.
  CREATE_STORIES_AND_UPDATES: 'Create Stories & Updates',

  // 44-51 · General Report
  VIEW_GENERAL_REPORT: 'View General Report',
  VIEW_STATS: 'View Stats',
  // Singular. The Inventory module already has a separate, unrelated
  // "View Activity Logs" (plural) — do not merge the two.
  VIEW_ACTIVITY_LOG: 'View Activity Log',
  // NOTE: this exact name is already in use by Reports > PhoneBook Call
  // Report (pages/Reports/ReportLayout.jsx). The permission map is flat and
  // keyed by name, so one grant opens both screens. Flagged for the backend
  // — renaming either one is a catalogue decision, not a frontend one.
  VIEW_CALL_REPORT: 'View Call Report',
  VIEW_CHANNEL_REPORT: 'View Channel Report',
  VIEW_CALL_SUMMARY: 'View Call Summary',
  VIEW_REGISTERED_USERS: 'View Registered Users',
  VIEW_WHATSAPP_REPORT: 'View Whatsapp Report',

  // 52-53 · Contacts
  VIEW_CONTACT_MANAGEMENT: 'View Contact Management',
  VIEW_CONTACT_SEARCH: 'View Contact Search',

  // 54-60 · HR
  VIEW_HR: 'View HR',
  VIEW_HR_DASHBOARD: 'View HR Dashboard',
  VIEW_CANDIDATE: 'View Candidate',
  VIEW_INTERESTED_CANDIDATE: 'View Interested Candidate',
  VIEW_JOB_APPLICANT: 'View Job Applicant',
  VIEW_HR_WHATSAPP_PROMOTION: 'View HR Whatsapp Promotion',
  VIEW_HR_SMS_PROMOTION: 'View HR SMS Promotion',
};

/** Every CRM > Admin permission name, for the sidebar section's union gate. */
export const ALL_CRM_PERMISSIONS = Object.values(CRM_PERM);

export default CRM_PERM;
